import { cloneDeep } from 'lodash-es';
import { FC, default as React, useState } from 'react';
import {
	CountryIndexResponse,
	RateSheetGeneratorProfileResponse,
	RsgRoutingOptions,
	SubscriptionIndexResponse
} from 'RtModels';
import { RateSheetGeneratorResource } from 'RtUi/app/AccountManagement/RateSheetGenerator/libs/resources/RateGeneratorResource';
import { useGetCountries } from 'RtUi/app/user/service';
import { RtxBooleanInput } from 'RtUi/components/rtx/inputs/Boolean/RtxBooleanInput';
import { RtxPrecisionInput } from 'RtUi/components/rtx/inputs/Precision/RtxPrecisionInput';
import { RtxRateMultipliersInput } from 'RtUi/components/rtx/inputs/RtxRateMultipliersInput';
import { RtxCountrySelect } from 'RtUi/components/rtx/inputs/Select/instances/Country/RtxCountrySelect';
import { RtxSubscriptionSelect } from 'RtUi/components/rtx/inputs/Select/instances/Subscription/RtxSubscriptionSelect';
import { RtxTextInput } from 'RtUi/components/rtx/inputs/Text/RtxTextInput';
import { RtForm } from 'RtUi/components/ui/RtUiForm/RtForm';

interface IRateSheetGeneratorEditorProps {
	onUpdate: (updatedResource: RateSheetGeneratorProfileResponse) => void;
	disablePadding?: boolean;
	editMode?: RateSheetGeneratorProfileResponse;
	onCancel?: () => void;
	createMode?: boolean;
	narrow?: boolean;
	displayModes?: boolean;
}

const resource = new RateSheetGeneratorResource();

export const RateSheetGeneratorEditor: FC<
	React.PropsWithChildren<IRateSheetGeneratorEditorProps>
> = ({
	narrow,
	editMode,
	onUpdate,
	onCancel = () => null,
	createMode = false,
	displayModes = false
}) => {
	const [editingEntity, setEditingEntity] = useState<
		Partial<RateSheetGeneratorProfileResponse>
	>(editMode ? cloneDeep(editMode) : {});

	const [error, setError] = useState();
	const [displayMode, setDisplayMode] = useState<boolean>(displayModes);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [subscriptionId, setSubscription] =
		useState<SubscriptionIndexResponse>();

	const countries = useGetCountries();

	const [applyMp, setApplyMp] = useState<boolean>(
		editingEntity.routingOptions?.applyMp || false
	);
	const [applyRr, setApplyRr] = useState<boolean>(
		editingEntity.routingOptions?.applyRr || false
	);
	const [applySq, setApplySq] = useState<boolean>(
		editingEntity.routingOptions?.applySq || false
	);
	const [applySqRd, setApplySqRd] = useState<boolean>(
		editingEntity.routingOptions?.applySqRd || false
	);

	const [marginPercent, setMarginPercent] = useState<number>(
		editingEntity.marginPercent ? editingEntity.marginPercent * 100 : 0
	);

	const tmpOnlyIsos: CountryIndexResponse[] = [];

	if (editingEntity.onlyIsos) {
		editingEntity.onlyIsos.forEach((iso: string) => {
			const country = countries.data?.find(
				(country) => country.iso3166Alpha3 === iso
			);
			if (country) {
				tmpOnlyIsos.push(country);
			}
		});
	}
	const [omitIsos, setOmitIsos] = useState<CountryIndexResponse[]>(
		editingEntity.omitIsos || []
	);
	const [onlyIsos, setOnlyIsos] = useState<CountryIndexResponse[]>(tmpOnlyIsos);

	const onChange = <K extends keyof RateSheetGeneratorProfileResponse>(
		key: K,
		value: RateSheetGeneratorProfileResponse[K]
	) => {
		if (!isSubmitting) {
			const updated = {
				[key]: value
			};
			setEditingEntity({
				...editingEntity,
				...updated
			});
		}
	};

	const getServiceIds = () => {
		return [5, 220];
	};

	const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		console.log(editingEntity);

		setIsSubmitting(true);
		setError(undefined);

		try {
			let newEntity: RateSheetGeneratorProfileResponse;
			if (createMode) {
				newEntity = await resource.createRateSheetGenerator(editingEntity);
			} else {
				newEntity = await resource.updateRateSheetGenerator(
					editingEntity.rateSheetGeneratorId!,
					editingEntity
				);
			}
			setIsSubmitting(false);
			onUpdate(newEntity);
			setDisplayMode(true);
		} catch (err: any) {
			setIsSubmitting(false);
			setError(err);
		}
	};

	const onCancelInternal = () => {
		setEditingEntity(cloneDeep(editMode!));
		setDisplayMode(true);
		onCancel();
	};

	return (
		<RtForm
			narrow={narrow}
			createMode={createMode}
			disablePadding={!createMode}
			onCancel={onCancelInternal}
			isSubmitting={isSubmitting}
			displayMode={displayMode}
			onSubmit={onSubmit}
			error={error}
			onChange={(dMode) => {
				setDisplayMode(dMode);
			}}
		>
			<div style={{ display: 'flex', gap: 24 }}>
				<div style={{ flex: 1, minWidth: 0 }}>
					{/* {!createMode && (
						<InputFormControl
							label="ID"
							readOnly={true}
							displayMode={true}
							value={String(editingEntity.rateSheetGeneratorId)}
							style={{ display: 'none' }}
							required
						/>
					)} */}
					<RtxTextInput
						label="Label"
						displayMode={displayMode}
						value={editingEntity?.label ? String(editingEntity.label) : ''}
						onChange={(value) => onChange('label', value)}
						name="label"
						required
					/>
					<br />
					<br />
					<RtxBooleanInput
						label="Active"
						displayMode={displayMode}
						value={editingEntity.isActive}
						onChange={(value) => onChange('isActive', value)}
					/>
					<br />
					<br />
					<RtxSubscriptionSelect
						label="Subscription"
						displayMode={displayMode}
						value={subscriptionId}
						onChange={(value) => {
							setSubscription(value);
							onChange('subscriptionId', value?.subscriptionId);
						}}
						serviceIds={getServiceIds()}
						initialOptionId={
							editingEntity.subscriptionId
								? Number(editingEntity.subscriptionId)
								: undefined
						}
					/>
					<br />
					<br />
					<RtxTextInput
						label="Output File Name"
						displayMode={displayMode}
						value={
							editingEntity?.rsgFileNameFmt
								? String(editingEntity.rsgFileNameFmt)
								: ''
						}
						onChange={(value) => onChange('rsgFileNameFmt', value)}
						name="rsgFileNameFmt"
						required
					/>
					<br />
					<br />
					<RtxBooleanInput
						label="Apply Margin Protection"
						displayMode={displayMode}
						value={applyMp ? 1 : 0}
						onChange={(value) => {
							setApplyMp(value === 1);
						}}
					/>
					<br />
					<RtxBooleanInput
						label="Apply Routing Rules"
						displayMode={displayMode}
						value={applyRr ? 1 : 0}
						onChange={(value) => {
							setApplyRr(value === 1);
						}}
					/>
					<br />
					<RtxBooleanInput
						label="Apply QoS Matching"
						displayMode={displayMode}
						value={applySq ? 1 : 0}
						onChange={(value) => {
							setApplySq(value === 1);
						}}
					/>
					<br />
					<RtxBooleanInput
						label="Apply QoS Route Depth"
						displayMode={displayMode}
						value={applySqRd ? 1 : 0}
						onChange={(value) => {
							setApplySqRd(value === 1);
						}}
					/>
					<br />
					<br />
				</div>
				<div style={{ flex: 1, minWidth: 0 }}>
					<RtxPrecisionInput
						label="Margin %"
						value={marginPercent}
						onChange={(value) => setMarginPercent(Number(value))}
						displayMode={displayMode}
						allowCurrency={false}
						required
						precision={2}
					/>
					<br />
					<br />
					<RtxPrecisionInput
						label="Margin Minimum"
						value={editingEntity.marginMinimum}
						onChange={(value) => onChange('marginMinimum', Number(value))}
						displayMode={displayMode}
						allowCurrency={false}
						required
						precision={6}
					/>
					<br />
					<br />
					<RtxPrecisionInput
						label="Vendor Rate Min"
						value={editingEntity.vendorRateMin}
						onChange={(value) => onChange('vendorRateMin', Number(value))}
						displayMode={displayMode}
						allowCurrency={false}
						required
						precision={6}
					/>
					<br />
					<br />
					<RtxPrecisionInput
						label="Vendor Rate Max"
						value={editingEntity.vendorRateMax}
						onChange={(value) => onChange('vendorRateMax', Number(value))}
						displayMode={displayMode}
						allowCurrency={false}
						required
						precision={6}
					/>
					<br />
					<br />
					<RtxPrecisionInput
						label="Vendor Cover Min"
						value={editingEntity.vendorCoverMin}
						onChange={(value) => onChange('vendorCoverMin', Number(value))}
						displayMode={displayMode}
						allowCurrency={false}
						required
						precision={0}
					/>
					<br />
					<br />
					<RtxPrecisionInput
						label="Vendor Depth Max"
						value={editingEntity.vendorDepthMax}
						onChange={(value) => onChange('vendorDepthMax', Number(value))}
						displayMode={displayMode}
						allowCurrency={false}
						required
						precision={0}
					/>
					<br />
					<br />
					<RtxPrecisionInput
						label="Rounding Precision"
						value={editingEntity.roundingPrecision}
						onChange={(value) => onChange('roundingPrecision', Number(value))}
						displayMode={displayMode}
						allowCurrency={false}
						required
						precision={0}
					/>
					<br />
					<br />
					<RtxBooleanInput
						label="NANP"
						displayMode={displayMode}
						value={editingEntity.isNanp}
						onChange={(value) => onChange('isNanp', value)}
					/>
					<br />
					<RtxBooleanInput
						label="IDDD"
						displayMode={displayMode}
						value={editingEntity.isIddd}
						onChange={(value) => onChange('isIddd', value)}
					/>
					<br />
					<br />
					<RtxCountrySelect<'iso3166Alpha3', true>
						isMulti
						label="Omit Countries"
						displayMode={displayMode}
						className="mb-3"
						value={omitIsos}
						onChange={(iso) => {
							setOmitIsos(iso);
							onChange(
								'omitIsos',
								iso.map((country) => country.iso3166Alpha3)
							);
						}}
						initialOptionId={omitIsos.map((country) => country.iso3166Alpha3)}
					/>
					<RtxCountrySelect<'iso3166Alpha3', true>
						isMulti
						label="Only Countries"
						displayMode={displayMode}
						className="mb-3"
						value={onlyIsos}
						onChange={(iso) => {
							setOnlyIsos(iso);
							onChange(
								'onlyIsos',
								iso.map((country) => country.iso3166Alpha3)
							);
						}}
						initialOptionId={onlyIsos.map((country) => country.iso3166Alpha3)}
					/>
					<br />
					<br />
				</div>
			</div>
			<div>
				<br />
				<RtxRateMultipliersInput
					label="Rate Multipliers"
					displayMode={displayMode}
					currentValues={
						(editingEntity.rateMultipliers as unknown as Array<
							Array<number>
						>) || []
					}
				/>
			</div>
		</RtForm>
	);
};
